import {Component, EventEmitter, Input, OnInit, Output} from '@angular/core';
import {Chart} from 'angular-highcharts';

import * as moment from 'moment';
import {SeriesColumnOptions, XAxisOptions, YAxisPlotLinesOptions} from 'highcharts';
import {BehaviorSubject} from 'rxjs';

@Component({
    selector: 'app-consumption-alert-chart',
    templateUrl: './consumption-alert-chart.component.html',
    styleUrls: ['./consumption-alert-chart.component.scss']
})
export class ConsumptionAlertChartComponent implements OnInit {
    @Input() isDetail = false;
    @Output() chartLoaded = new EventEmitter<ConsumptionAlertChartComponent>(true);
    onLoaded = new BehaviorSubject<boolean>(false);

    chart: Chart = null;

    onlyShowStartEndDates = true;

    constructor() {
    }

    ngOnInit() {
        this.initializeChart();
    }

    deactivateXAxisLabelLimitation(): void {
        this.onlyShowStartEndDates = false;
    }

    addNewSeries(values: any[], y_attribute: string, remove_series_count = 0, zindex = null): void {
        if (remove_series_count >= 0) {
            this.removeSeries(0, remove_series_count);
        }
        try {
            const s: SeriesColumnOptions = {
                name: null,
                zIndex: 1,
                data: values.map((element) => {
                    return [
                        new Date(element.timestamp).getTime(),
                        element[y_attribute]
                    ];
                }),
                type: 'column'
            };
            if (zindex) {
                s.zIndex = zindex;
            }
            this.chart.addSeries(s, true, false);
            if (this.onlyShowStartEndDates) {
                const firstDate = new Date(values.first().timestamp).getTime();
                const lastDate = new Date(values.last().timestamp).getTime();
                const tickPositions = [firstDate, lastDate];
                this.chart.ref.update({xAxis: {tickPositions}}, true);
            }
        } catch (exc) {
            console.log('Exception: Malformed data.');
        }
    }

    removeSeries(idx: number, count = -1): void {
        if (count === -1) {
            this.chart.removeSeries(idx);
        } else {
            for (let i = idx; i < idx + count; ++i) {
                if (this.chart !== null && this.chart !== undefined) {
                    this.chart.removeSeries(i);
                }
            }
        }
    }

    reset(): void {
        this.chart.ref.showLoading('Auswertung wird geladen…');
    }

    showLoadingState(show: boolean = true): void {
        if (show) {
            if (this.chart) {
                if (this.chart.ref) {
                    this.chart.ref.showLoading('Keine Auswertung für diesen Zeitraum verfügbar!');
                }
            }
        } else {
            if (!this.chart) {
                return;
            }
            if (this.chart.ref) {
                this.chart.ref.hideLoading();
            }
        }
    }

    updateMaxValueLine(value, state: boolean): void {
        this.chart.ref.update({yAxis: {plotLines: []}});
        const valFmt = value.toLocaleString('de-DE').split(',');
        // const color = state ? '#0097A7' : '#b00402';
        const color = state ? '#0097A7' : '#b00402';
        const valueStr = `${valFmt[0]} Watt`;
        const stateClass = state ? 'positive' : 'negative';
        const container = `<div class="consumption-alert-callout ${stateClass}">${valueStr}</div>`;
        const options: YAxisPlotLinesOptions = {
            color,
            width: 2,
            value,
            zIndex: 100,
            label: {
                align: 'left',
                useHTML: true,
                text: container,
            },

        };
        this.chart.ref.update({yAxis: {plotLines: [options], max: value * 1.01}},
            true, true, true);
    }

    insertVerticalLine(time: Date): void {
        const options: XAxisOptions = {
            plotLines: [{color: 'rgba(0,0,0,0.20)', width: 2, value: time.getTime(),}]
        };
        this.chart.ref.update({xAxis: options}, true);
    }

    private initializeChart(): void {
        const self = this;
        this.chart = new Chart({
            chart: {
                type: 'column',
                backgroundColor: 'rgba(255, 255, 255, 0)',
                margin: [30, 0, 50, 40],
                reflow: true,
                events: {
                    load() {
                        this.showLoading('Auswertung wird geladen…');
                        self.onLoaded.next(true);
                        self.chartLoaded.emit(self);
                    }
                }
            },
            title: {
                text: null
            },
            xAxis: {
                type: 'datetime',
                showFirstLabel: true,
                showLastLabel: true,
                tickAmount: this.onlyShowStartEndDates ? 2 : null,
                tickInterval: 1000 * 60 * 60 * 2,
                dateTimeLabelFormats: {
                    millisecond: '%H:%M',
                    second: '%H:%M',
                    minute: '%H:%M',
                    hour: '%H:%M',
                    day: '%d.%m',
                    week: '%d.%m',
                    month: '%m.%Y',
                    year: '%Y',
                },
                labels: {
                    style: {
                        fontSize: '13px',
                        fontFamily: 'EONBrixSans, sans-serif',
                        fontWeight: 'bold'
                    },
                    formatter: (ref) => {
                        const startoftoday = moment().startOf('day');
                        const parsed = moment(ref.value);
                        const differenceDays = moment(startoftoday).day() - moment(parsed).day();
                        const timeString = parsed.format('HH:mm');
                        if (ref.isFirst || ref.isLast) {
                            let prefix = '';
                            if (differenceDays === 1) {
                                prefix = 'Gestern';
                            } else if (differenceDays === 2) {
                                prefix = 'Vorgestern';
                            } else if (differenceDays > 2) {
                                prefix = parsed.format('DD.MM.YYYY');
                            }
                            return `${prefix} \n ${timeString}`;
                        }
                        if (!this.onlyShowStartEndDates) {
                            return parsed.format('HH:mm');
                        }
                    },
                    autoRotation: false,
                    overflow: 'justify'
                },
                crosshair: {
                    width: 1,
                    color: 'rgba(0,0,0,0.6)',
                    zIndex: 100
                },
                minPadding: 0,
                maxPadding: 0
            },
            yAxis: {
                title: {
                    text: 'Watt',
                    align: 'high',
                    offset: 0,
                    rotation: 0,
                    y: -20,
                    x: -8,
                    style: {
                        fontFamily: 'EONBrixSans, sans-serif',
                    }
                },
                tickAmount: 4,
                labels: {
                    align: 'right',
                    x: -8,
                    y: 6,
                    step: 1,
                    formatter() {
                        if (this.value >= 1) {
                            return this.value.toLocaleString('de-DE');
                        } else {
                            return null;
                        }
                    },
                    style: {
                        fontSize: '13px',
                        fontFamily: 'EONBrixSans, sans-serif',
                        fontWeight: 'bold'
                    }
                },
                gridZIndex: 10,
                gridLineColor: 'rgba(0,0,0,0.20)'
            },
            tooltip: {
                hideDelay: 0,
                enabled: true,
                animation: false,
                shadow: true,
                padding: 0,
                positioner(boxWidth: number, boxHeight: number, point: any) {
                    if ((point.plotX + this.chart.plotLeft + boxWidth) > this.chart.plotWidth) {
                        // old
                        // x:
                        return {
                            x: (point.plotX + this.chart.plotLeft) - boxWidth - 10,
                            y: point.plotY + (boxHeight / 2)
                        };
                    } else {
                        // old
                        // x: point.plotX + this.chart.plotLeft + 10,
                        return {
                            x: point.plotX + this.chart.plotLeft + 10,
                            y: point.plotY + (boxHeight / 2)
                        };
                    }
                },
                useHTML: true,
                formatter() {
                    const d = moment(this.key).format('DD.MM.YY HH:mm');
                    const style = `style="border-color: ${this.color}"`;
                    const name = `<div class="header">${d} Uhr</div>`;
                    const value = `<div class="body"> ${this.y.toLocaleString('de-DE')} Watt</div>`;
                    return `<div class="column-callout" ${style}> ${name} ${value}</div>`;
                },
                backgroundColor: 'transparent',
                borderWidth: 0,
                borderRadius: 0
            },
            plotOptions: {
                column: {
                    pointPadding: 0,
                    groupPadding: 0,
                    borderWidth: 0,
                    borderColor: '#ffffff',
                    color: '#e3e000',
                    states: {
                        hover: {
                            enabled: false
                        }
                    }
                },
                series: {
                    showInLegend: false,
                    animation: false,
                    marker: {
                        states: {
                            hover: {
                                enabled: false
                            }
                        }
                    },
                    states: {
                        hover: {
                            enabled: false
                        }
                    }
                }
            },
            series: [
                {
                    data: null,
                    name: null,
                    type: 'column'
                },
            ],
            credits: {
                enabled: false
            }
        });
    }

}

